import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { successToast } from "../components/SuccessCheck";
import { FileDown, Link2, Tablet, Ban } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import FormDocument from "../components/FormDocument";
import SignaturePad from "../components/SignaturePad";
import { SF_FIELDS } from "../lib/showroomForms";

import { tr } from "../lib/i18n";
const MANAGERS = ["admin", "management"];
const inputCls = "h-11 rounded-lg border px-3 w-full bg-white";

export async function downloadSfPdf(f) {
  try {
    const { data } = await api.get(`/showroom-forms/${f.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = `${f.reference}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error(formatApiError(e)); }
}

function editableMap(type) {
  const m = {};
  SF_FIELDS[type].forEach(([, , rows]) => rows.forEach(([k, , , t]) => { if (!t.startsWith("ro")) m[k] = true; }));
  return m;
}

function NewContract({ onCreated }) {
  const { t } = useT();
  const [opts, setOpts] = useState({ vehicles: [], customers: [] });
  const [v, setV] = useState({ vehicle_id: "", buyer_id: "", selling_price: "", fine_amount: "" });
  useEffect(() => {
    Promise.all([api.get("/vehicles", { params: { limit: 200 } }), api.get("/customers", { params: { limit: 200 } })])
      .then(([a, b]) => setOpts({ vehicles: (a.data.items || []).filter((x) => x.status !== "Sold"), customers: b.data.items || [] }))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);
  const create = async () => {
    try {
      const data = { selling_price: v.selling_price || undefined, fine_amount: v.fine_amount || undefined };
      const { data: f } = await api.post("/showroom-forms/sale_contract", { vehicle_id: v.vehicle_id, buyer_id: v.buyer_id, data });
      onCreated(f);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="panel grid gap-3 max-w-xl" style={{ padding: 20 }} data-testid="sf-new-contract">
      <label className="text-sm">{t("sf.pick_vehicle")}
        <select data-testid="sf-pick-vehicle" className={inputCls} value={v.vehicle_id} onChange={(e) => setV({ ...v, vehicle_id: e.target.value })}>
          <option value="">—</option>
          {opts.vehicles.map((x) => <option key={x.id} value={x.id}>{x.stock_number} · {x.make} {x.model} {x.model_year}</option>)}
        </select></label>
      <label className="text-sm">{t("sf.pick_buyer")}
        <select data-testid="sf-pick-buyer" className={inputCls} value={v.buyer_id} onChange={(e) => setV({ ...v, buyer_id: e.target.value })}>
          <option value="">—</option>
          {opts.customers.map((x) => <option key={x.id} value={x.id}>{x.name} · {x.mobile}</option>)}
        </select></label>
      <label className="text-sm">{t("sf.selling_price")}<input data-testid="sf-new-price" inputMode="decimal" className={inputCls} value={v.selling_price} onChange={(e) => setV({ ...v, selling_price: e.target.value })} /></label>
      <label className="text-sm">{t("sf.fine_amount")}<input data-testid="sf-new-fine" inputMode="decimal" className={inputCls} value={v.fine_amount} onChange={(e) => setV({ ...v, fine_amount: e.target.value })} /></label>
      <button data-testid="sf-create" className="btn-primary h-11" disabled={!v.vehicle_id || !v.buyer_id} onClick={create}>{t("sf.create")}</button>
    </div>
  );
}

function ManagerTools({ f, reload }) {
  const { t } = useT();
  const [com, setCom] = useState({ commission_type: f.data.commission_type || "Percentage", commission_value: f.data.commission_value ?? "" });
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/users").then(({ data }) => setUsers((data.items || data || []).filter((u) => u.is_active !== false && ["sales", "management"].includes(u.role)))).catch(() => {}); }, []);
  const post = async (path, body) => { try { await api.post(`/showroom-forms/${f.id}/${path}`, body); toast.success(t("sf.saved")); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="panel grid gap-3" style={{ padding: 16 }} data-testid="sf-manager-tools">
      {f.form_type === "owner_auth" && (
        <div className="flex flex-wrap gap-2 items-end" style={{ alignItems: "flex-end" }}>
          <label className="text-sm">{t("sf.commission")}
            <select data-testid="sf-commission-type" className={inputCls} value={com.commission_type} onChange={(e) => setCom({ ...com, commission_type: e.target.value })}>
              <option value="Percentage">{t("sf.percentage")}</option><option value="Fixed Amount">{t("sf.fixed")}</option>
            </select></label>
          <input data-testid="sf-commission-value" inputMode="decimal" aria-label={t("sf.commission")} className={inputCls} style={{ width: 140 }} value={com.commission_value} onChange={(e) => setCom({ ...com, commission_value: e.target.value })} />
          <button data-testid="sf-commission-save" className="h-11 px-4 border rounded-lg text-sm font-semibold" style={{ height: 44 }} onClick={() => post("commission", com)}>{t("sf.set_commission")}</button>
        </div>
      )}
      <label className="text-sm">{t("sf.salesperson")}
        <select data-testid="sf-salesperson" className={inputCls} value={f.salesperson_id || ""} onChange={(e) => post("salesperson", { user_id: e.target.value })}>
          {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select></label>
    </div>
  );
}

function SignBox({ f, party, label, reload }) {
  const { t } = useT();
  const [sig, setSig] = useState(null);
  const sign = async () => { try { const { data } = await api.post(`/showroom-forms/${f.id}/sign`, { party, signature: sig }); (data?.status === "Completed" ? successToast : toast.success)(t("sf.saved")); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="panel" style={{ padding: 16 }}>
      <SignaturePad label={label} onChange={setSig} testid={`sf-pad-${party}`} />
      <button data-testid={`sf-sign-${party}`} className="btn-primary h-11 px-4 mt-2" disabled={!sig} onClick={sign}>{label}</button>
    </div>
  );
}

export default function ShowroomFormPage() {
  const { id, type } = useParams();
  const nav = useNavigate();
  const { t } = useT();
  const { user } = useAuth();
  const [f, setF] = useState(null);
  const [showroom, setShowroom] = useState({});
  const [vals, setVals] = useState({});
  const [texts, setTexts] = useState(null);
  const load = useCallback(async () => {
    if (!id) return;
    try { const { data } = await api.get(`/showroom-forms/${id}`); setF(data); setVals(data.data || {}); } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/settings").then(({ data }) => setShowroom(data || {})).catch(() => {});
    api.get("/showroom-forms/texts").then(({ data }) => setTexts(data)).catch(() => {});
  }, []);
  const created = (doc) => nav(`/forms/sf/${doc.id}`, { replace: true });

  if (!id && type === "contract") return <div className="space-y-4"><h1 className="text-3xl font-semibold">{t("sf.contract_title")}</h1><NewContract onCreated={created} /></div>;
  if (!id) {
    const draft = { form_type: "owner_auth", reference: t("sf.ref_pending"), dates: texts?.today || {}, texts: texts?.owner_auth || {}, data: vals };
    const create = async () => { try { const { data } = await api.post("/showroom-forms/owner_auth", { data: vals }); created(data); } catch (e) { toast.error(formatApiError(e)); } };
    return (
      <div className="space-y-4" data-testid="sf-new-owner">
        <h1 className="text-3xl font-semibold">{t("sf.owner_title")}</h1>
        <FormDocument form={draft} showroom={showroom} editable={editableMap("owner_auth")} values={vals} onChange={(k, v) => setVals({ ...vals, [k]: v })} hideSignatures />
        <button data-testid="sf-create" className="btn-primary h-11 px-6" onClick={create}>{t("sf.create")}</button>
      </div>
    );
  }
  if (!f) return <div className="opacity-60">…</div>;
  const sigs = f.signatures || {};
  const isDraft = f.status === "Draft" && !Object.keys(sigs).length;
  const staff = ["admin", "management", "sales"].includes(user?.role);
  const save = async () => { try { await api.patch(`/showroom-forms/${f.id}`, { data: vals }); toast.success(t("sf.saved")); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const link = async () => {
    try {
      const { data } = await api.post(`/showroom-forms/${f.id}/link`);
      const url = `${window.location.origin}${data.path}`;
      await navigator.clipboard?.writeText(url).catch(() => {});
      window.prompt(t("sf.link_copied"), url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const voidIt = async () => {
    const reason = window.prompt(t("sf.void_reason"));
    if (!reason) return;
    try { const { data } = await api.post(`/showroom-forms/${f.id}/void`, { reason }); nav(`/forms/sf/${data.id}`); } catch (e) { toast.error(formatApiError(e)); }
  };
  const tablet = () => { sessionStorage.setItem("kiosk_lock", f.id); document.documentElement.requestFullscreen?.().catch(() => {}); nav(`/kiosk/${f.id}`); };
  return (
    <div className="space-y-4" data-testid="sf-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow">{t(`sf.status_${f.status.toLowerCase() /* i18n-keys: draft,signed,completed,voided */}`)}{f.version > 1 ? ` · ${t("sf.version")} ${f.version}` : ""}</div>
          <h1 className="text-3xl font-semibold">{f.form_type === "owner_auth" ? t("sf.owner_title") : t("sf.contract_title")} <span dir="ltr">{f.reference}</span></h1>
          {f.void_reason && <p className="text-sm text-red-600" data-testid="sf-void-reason">{f.void_reason}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="sf-pdf" className="h-11 px-4 rounded-lg border flex items-center gap-2" onClick={() => downloadSfPdf(f)}><FileDown size={16} /> {t("sf.pdf")}</button>
          {staff && !sigs.customer && f.status === "Draft" && <>
            <button data-testid="sf-tablet" className="h-11 px-4 rounded-lg border flex items-center gap-2" onClick={tablet}><Tablet size={16} /> {t("sf.tablet")}</button>
            <button data-testid="sf-link" className="h-11 px-4 rounded-lg border flex items-center gap-2" onClick={link}><Link2 size={16} /> {t("sf.link")}</button>
          </>}
          {MANAGERS.includes(user?.role) && f.status !== "Voided" && <button data-testid="sf-void" className="h-11 px-4 rounded-lg border flex items-center gap-2 text-red-600" onClick={voidIt}><Ban size={16} /> {t("sf.void")}</button>}
          {f.vehicle_id && <button data-testid="sf-open-car" className="h-11 px-4 rounded-lg border" onClick={() => nav(`/inventory/${f.vehicle_id}`)}>{t("sf.open_car")}</button>}
        </div>
      </div>
      <FormDocument form={f} showroom={showroom} editable={isDraft && staff ? editableMap(f.form_type) : {}} values={vals} onChange={(k, v) => setVals({ ...vals, [k]: v })} />
      {isDraft && staff && <button data-testid="sf-save" className="btn-primary h-11 px-6" onClick={save}>{t("sf.save")}</button>}
      {MANAGERS.includes(user?.role) && f.status !== "Voided" && <ManagerTools f={f} reload={load} />}
      {f.manager_signature_mode === "required" && sigs.staff && !sigs.manager && f.status !== "Voided" && (
        <div className="panel p-4 border-s-4" style={{ borderInlineStartColor: "var(--p)" }} data-testid="sf-waiting-manager">
          <div className="font-semibold">{t("sf.waiting_manager")}</div>
          <div className="text-sm opacity-80">{t("sf.waiting_manager_hint")}</div>
        </div>
      )}
      {f.status !== "Voided" && f.status !== "Completed" && (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
          {staff && !sigs.staff && (f.form_type === "owner_auth" || sigs.customer) && <SignBox f={f} party="staff" label={t("sf.sign_staff")} reload={load} />}
          {MANAGERS.includes(user?.role) && !sigs.manager && f.manager_signature_mode !== "not_used"
            && (f.manager_signature_mode !== "required" || sigs.staff) && <SignBox f={f} party="manager" label={t("sf.sign_manager")} reload={load} />}
        </div>
      )}
    </div>
  );
}
